import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Copy, DollarSign, MousePointerClick, Package, Share2 } from "lucide-react";
import { useAuth, formatApiError } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL_SNIPPETS = (link) => [
  {
    label: "Twitter / X (short)",
    text: `Just found the fix for messy mic-stand cords → Aura Sleeve by @stageletics. Clean stages, fast setup. ${link}`,
  },
  {
    label: "Instagram / Facebook",
    text: `If you've ever squinted at a stage during a service and wondered why the cords look so distracting — this is the answer. The Aura Sleeve covers every wire on your mic stands in seconds. Cleaner stages, zero fuss. ${link}`,
  },
  {
    label: "Newsletter / email",
    text: `Hey team — sharing a small thing that's made a huge difference for our stage look: the Aura Sleeve. It's a matte-black cord cover for microphone stands. Two minutes to install, night and day on camera. ${link}`,
  },
];

export default function AffiliateDashboard() {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [me, comms] = await Promise.all([
        axios.get(`${API}/affiliate/me`, { headers: authHeader() }),
        axios.get(`${API}/affiliate/me/commissions`, { headers: authHeader() }),
      ]);
      setData(me.data);
      setCommissions(comms.data);
    } catch (e) {
      setError(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const refLink = useMemo(() => {
    if (!data?.affiliate?.code) return "";
    return `${window.location.origin}/?ref=${data.affiliate.code}`;
  }, [data]);
  const qrUrl = refLink
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&bgcolor=050505&color=E8A851&data=${encodeURIComponent(refLink)}`
    : "";

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success("Copied to clipboard"),
      () => toast.error("Copy failed — please copy manually")
    );
  };

  if (loading) {
    return <div className="pt-32 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading dashboard…</div>;
  }
  if (error) {
    return (
      <div className="bg-[#050505] text-white pt-32 min-h-screen">
        <div className="px-6 md:px-12 lg:px-24 max-w-2xl mx-auto text-center">
          <p className="eyebrow mb-4">Affiliate</p>
          <h1 className="font-display text-3xl mb-4">You don't have an affiliate account yet.</h1>
          <p className="text-zinc-400 mb-8 text-sm">{error}</p>
          <Link to="/affiliate" data-testid="affiliate-apply-cta" className="btn-primary inline-flex">Apply now</Link>
        </div>
      </div>
    );
  }

  const { affiliate, stats } = data;
  const isPending = affiliate.status === "pending";
  const isRejected = affiliate.status === "rejected";

  return (
    <div data-testid="affiliate-dashboard-page" className="bg-[#050505] text-white pt-32 min-h-screen">
      <div className="px-6 md:px-12 lg:px-24 pb-24 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <p className="eyebrow mb-3">Affiliate console</p>
          <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight">
            {affiliate.name?.split(" ")[0]}'s dashboard
          </h1>
          <p className="text-zinc-400 mt-3 text-sm">
            Code: <span className="font-mono text-white">{affiliate.code}</span> ·{" "}
            Status:{" "}
            <span
              data-testid="affiliate-status"
              className={
                affiliate.status === "active" ? "text-emerald-400" :
                isPending ? "text-[#E8A851]" : "text-red-400"
              }
            >
              {affiliate.status}
            </span>
          </p>
        </div>

        {isPending && (
          <div data-testid="affiliate-pending-banner" className="border border-[#E8A851]/40 bg-[#E8A851]/[0.05] p-6 mb-10">
            <p className="text-[#E8A851] font-medium mb-2">Waiting for approval</p>
            <p className="text-sm text-zinc-300">
              Your ref link is not live yet. Once an admin approves you (usually within 1 business day),
              clicks and commissions will start being tracked.
            </p>
          </div>
        )}
        {isRejected && (
          <div className="border border-red-400/40 bg-red-400/[0.05] p-6 mb-10">
            <p className="text-red-300 font-medium">Application not approved</p>
            <p className="text-sm text-zinc-300 mt-2">Reach out to orders@stageletics.com if you have questions.</p>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <StatCard testid="stat-clicks" icon={MousePointerClick} label="Clicks" value={stats.clicks} />
          <StatCard testid="stat-orders" icon={Package} label="Referred orders" value={stats.referred_orders} />
          <StatCard testid="stat-earned" icon={DollarSign} label="Total earned" value={`$${stats.total_earned.toFixed(2)}`} />
          <StatCard testid="stat-unpaid" icon={DollarSign} label="Unpaid balance" value={`$${stats.unpaid_balance.toFixed(2)}`} accent />
        </div>

        {/* Share tools */}
        <div className="border border-white/10 bg-white/[0.02] p-8 mb-10">
          <p className="eyebrow mb-6"><Share2 className="w-4 h-4 inline mr-2" />Share tools</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Ref link + copy */}
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2 block">Your ref link</label>
              <div className="flex gap-2">
                <input
                  data-testid="affiliate-ref-link"
                  readOnly
                  value={refLink}
                  className="flex-1 bg-black/40 border border-white/10 px-3 py-3 text-sm text-white font-mono"
                  onClick={(e) => e.target.select()}
                />
                <button
                  data-testid="affiliate-copy-link"
                  onClick={() => copy(refLink)}
                  className="btn-secondary !text-[#E8A851] !border-[#E8A851]/40"
                >
                  <Copy className="w-4 h-4" /> Copy
                </button>
              </div>

              <div className="mt-8 space-y-5">
                <p className="text-xs uppercase tracking-[0.15em] text-zinc-500">Pre-written snippets</p>
                {SOCIAL_SNIPPETS(refLink).map((s, i) => (
                  <div key={s.label} data-testid={`affiliate-snippet-${i}`} className="border border-white/10 p-4 bg-white/[0.01]">
                    <div className="flex justify-between items-start gap-4 mb-2">
                      <p className="text-xs uppercase tracking-[0.15em] text-[#E8A851]">{s.label}</p>
                      <button
                        data-testid={`affiliate-snippet-copy-${i}`}
                        onClick={() => copy(s.text)}
                        className="text-xs text-zinc-400 hover:text-white flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                    <p className="text-sm text-zinc-300 leading-relaxed">{s.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* QR */}
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2 block">QR code</label>
              {qrUrl && (
                <div className="border border-white/10 p-4 bg-black/30 inline-block">
                  <img
                    data-testid="affiliate-qr-image"
                    src={qrUrl}
                    alt="QR code for ref link"
                    className="w-full max-w-[200px] block"
                  />
                </div>
              )}
              <p className="text-xs text-zinc-500 mt-3">Print, add to your merch table or stage-side sign.</p>
            </div>
          </div>
        </div>

        {/* Commission history */}
        <div className="border border-white/10 bg-white/[0.02]">
          <div className="p-5 border-b border-white/10">
            <p className="eyebrow">Commission history</p>
          </div>
          {commissions.length === 0 ? (
            <div data-testid="affiliate-no-commissions" className="p-12 text-center text-sm text-zinc-500">
              No commissions yet. Once someone buys through your ref link, it'll show up here.
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {commissions.map((c) => (
                <div key={c.id} data-testid={`affiliate-commission-${c.id}`} className="p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                  <div className="md:col-span-3 text-xs text-zinc-500">{new Date(c.created_at).toLocaleDateString()}</div>
                  <div className="md:col-span-4 text-sm">Order subtotal: <span className="text-white">${Number(c.order_subtotal).toFixed(2)}</span></div>
                  <div className="md:col-span-3 text-sm text-zinc-400">Rate: {c.rate_used}% · sale #{c.sale_number}</div>
                  <div className="md:col-span-2 text-right">
                    <p className="font-display text-xl text-[#E8A851]">+${Number(c.commission_amount).toFixed(2)}</p>
                    <p className={`text-[10px] uppercase tracking-[0.15em] mt-1 ${c.status === "paid" ? "text-emerald-400" : "text-zinc-500"}`}>
                      {c.status}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className={`border p-5 ${accent ? "border-[#E8A851]/40 bg-[#E8A851]/[0.05]" : "border-white/10 bg-white/[0.02]"}`}
    >
      <Icon className={`w-5 h-5 mb-3 ${accent ? "text-[#E8A851]" : "text-zinc-400"}`} />
      <p className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1">{label}</p>
      <p className={`font-display text-2xl font-semibold ${accent ? "text-[#E8A851]" : "text-white"}`}>{value}</p>
    </div>
  );
}
